// admin/src/pages/GatewayMonitoring/components/GatewaySettingsDialog.js
'use strict';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { getSettings, updateSettings, extractGatewayErrorMessage } from '../gatewayApi';

export default function GatewaySettingsDialog({
  open = false,
  onClose,
  onSettingsUpdated,
  mode = 'light',
  canUpdate = false,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Form states
  const [retentionDays, setRetentionDays] = useState(30);
  const [offlineAfterSeconds, setOfflineAfterSeconds] = useState(180);
  const [failureSpikeCount, setFailureSpikeCount] = useState(20);
  const [lockoutSpikeCount, setLockoutSpikeCount] = useState(5);

  // Confirmation dialog for retention change
  const [originalRetention, setOriginalRetention] = useState(30);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);

  useEffect(() => {
    if (open) {
      loadCurrentSettings();
    } else {
      setError(null);
      setSuccessMsg(null);
      setConfirmOpen(false);
    }
  }, [open]);

  const loadCurrentSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSettings();
      if (res && res.data) {
        const d = res.data;
        const ret = Number(d.retentionDays) || 30;
        setRetentionDays(ret);
        setOriginalRetention(ret);

        if (d.alertThresholds) {
          setOfflineAfterSeconds(Number(d.alertThresholds.offlineAfterSeconds) || 180);
          setFailureSpikeCount(Number(d.alertThresholds.failureSpikeCount) || 20);
          setLockoutSpikeCount(Number(d.alertThresholds.lockoutSpikeCount) || 5);
        }
      }
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const validateThresholds = () => {
    if (offlineAfterSeconds < 30 || offlineAfterSeconds > 86400) {
      return 'Offline threshold must be between 30 and 86,400 seconds.';
    }
    if (failureSpikeCount < 1 || failureSpikeCount > 10000) {
      return 'Failure spike threshold must be between 1 and 10,000.';
    }
    if (lockoutSpikeCount < 1 || lockoutSpikeCount > 10000) {
      return 'Lockout spike threshold must be between 1 and 10,000.';
    }
    return null;
  };

  const handleSaveClick = () => {
    const validationErr = validateThresholds();
    if (validationErr) {
      setError(validationErr);
      return;
    }

    const payload = {
      retentionDays: Number(retentionDays),
      alertThresholds: {
        offlineAfterSeconds: Number(offlineAfterSeconds),
        failureSpikeCount: Number(failureSpikeCount),
        lockoutSpikeCount: Number(lockoutSpikeCount),
      },
    };

    // If retention changed, prompt confirmation dialog
    if (payload.retentionDays !== originalRetention) {
      setPendingSavePayload(payload);
      setConfirmOpen(true);
      return;
    }

    executeSave(payload);
  };

  const executeSave = async (payload) => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await updateSettings(payload);
      if (res && res.success) {
        setOriginalRetention(payload.retentionDays);
        setSuccessMsg('Gateway monitoring settings and retention updated successfully.');
        setConfirmOpen(false);
        setPendingSavePayload(null);
        onSettingsUpdated && onSettingsUpdated();
      }
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        data-testid="gateway-settings-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ color: 'primary.main' }} />
          Gateway Settings & Retention
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {successMsg && (
            <Alert severity="success" sx={{ mb: 2.5 }} onClose={() => setSuccessMsg(null)}>
              {successMsg}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Stack spacing={3}>
              {/* Event Retention Policy */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Event Retention Window
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Determines how long shipped telemetry and audit logs are retained in the cloud before automatic deletion.
                </Typography>

                <FormControl fullWidth size="small">
                  <InputLabel id="retention-days-label">Retention Period</InputLabel>
                  <Select
                    labelId="retention-days-label"
                    data-testid="retention-days-select"
                    inputProps={{ 'data-testid': 'retention-days-input' }}
                    value={retentionDays}
                    label="Retention Period"
                    disabled={!canUpdate || saving}
                    onChange={(e) => setRetentionDays(Number(e.target.value))}
                  >
                    <MenuItem value={30}>30 Days (Standard)</MenuItem>
                    <MenuItem value={60}>60 Days (Extended)</MenuItem>
                    <MenuItem value={90}>90 Days (Maximum)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Divider />

              {/* Alerting Thresholds */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Alert & Health Thresholds
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Parameters used by the background health monitor to raise gateway status alerts.
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Offline After (Seconds)"
                    helperText="Duration without heartbeat before gateway is marked Offline (30 - 86,400s)"
                    value={offlineAfterSeconds}
                    disabled={!canUpdate || saving}
                    onChange={(e) => setOfflineAfterSeconds(Number(e.target.value))}
                    inputProps={{ min: 30, max: 86400 }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Failure Spike Threshold"
                    helperText="Failed login attempts per 5-minute window to trigger alert (1 - 10,000)"
                    value={failureSpikeCount}
                    disabled={!canUpdate || saving}
                    onChange={(e) => setFailureSpikeCount(Number(e.target.value))}
                    inputProps={{ min: 1, max: 10000 }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Lockout Spike Threshold"
                    helperText="Security lockout events per 5-minute window to trigger alert (1 - 10,000)"
                    value={lockoutSpikeCount}
                    disabled={!canUpdate || saving}
                    onChange={(e) => setLockoutSpikeCount(Number(e.target.value))}
                    inputProps={{ min: 1, max: 10000 }}
                  />
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          {canUpdate && (
            <Button
              onClick={handleSaveClick}
              variant="contained"
              disabled={loading || saving}
              data-testid="save-settings-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Retention Policy Change Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="retention-confirm-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm Retention Change
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Are you sure you want to change the event retention period to <strong>{pendingSavePayload?.retentionDays} days</strong>?
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
            Events older than {pendingSavePayload?.retentionDays} days will be deleted automatically, and existing events are re-dated to the new retention.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            data-testid="confirm-retention-change-btn"
            disabled={saving}
            onClick={() => executeSave(pendingSavePayload)}
          >
            {saving ? 'Applying...' : 'Confirm & Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
